import * as React from "react"
import { Col, Container, Hidden, Row, Visible } from "react-grid-system"
import { useTranslation } from "react-i18next"
import { formatGroups } from "../../../utils/formatters"
import { GroupModel } from "../../../utils/models"
import { HorizontalSpacer, Spacer } from "../../atoms/spacers"
import SearchField from "../../atoms/inputs/SearchField"
import Dropdown, { Option, optionsMap } from "../../atoms/inputs/Dropdown"
import { Link, useNavigate, useParams } from "react-router-dom"
import PrimaryButton from "../../atoms/buttons/PrimaryButton"
import SecondaryButton from "../../atoms/buttons/SecondaryButton"
import LoadingSpinner from "../../atoms/LoadingSpinner"
import GroupOverviewCard from "../../organisms/GroupsOverviewCard"
import { useAllGroups } from "../../../hooks/useAllGroups"
import { PAGINATION_VALUE, PAGINATION_VALUE_PRESETS } from "../../../utils/design-helpers"
import PaginationComponent from "../../molecules/PaginationComponent"
import EmptyDashboardPage from "./EmptyDashboardPage"
import NoSearchResultPage from "./NoSearchResultPage"
import { captureExceptionWithContext } from "../../../utils/sentry"
import { Key } from "react-aria-components"

interface Props {
  suborgFilter: Array<number> | undefined
}

const DROPDOWN_OPTIONS: Option<string>[] = [
  { id: "name", value: "name", name: "Name A-Z" },
  { id: "-name", value: "-name", name: "Name Z-A" },
  { id: "-members", value: "-members", name: "Members high - low" },
  { id: "members", value: "members", name: "Members low - high" },
]

const GroupsPage: React.FunctionComponent<Props> = ({ suborgFilter }) => {
  const { t } = useTranslation()

  const { page } = useParams()
  const navigate = useNavigate()

  const [currentPage, setCurrentPage] = React.useState<number>(parseInt(page ?? "1"))
  const [offset, setoffset] = React.useState<number>(currentPage > 1 ? (currentPage - 1) * PAGINATION_VALUE : 0)

  const [searchValue, setSearchValue] = React.useState<undefined | string>(undefined)
  const [sortingId, setSortingId] = React.useState<Key>("name")

  const sorting = optionsMap(DROPDOWN_OPTIONS)[sortingId].value!

  const { allGroups, isLoading, error } = useAllGroups(PAGINATION_VALUE, offset, searchValue, sorting, suborgFilter)

  const formattedGroups = formatGroups(allGroups?.edges ?? [])

  if (error) {
    captureExceptionWithContext(error, "error fetching groups from backend")
  }

  //Handle pagination
  const totalCount = allGroups?.totalCount ?? 1
  const pagesInitialValue = Math.ceil(totalCount / PAGINATION_VALUE)
  const totalPages = totalCount < PAGINATION_VALUE ? 1 : pagesInitialValue
  const onClickPage = (newPage: number) => {
    if (newPage !== currentPage) {
      setoffset(offset ? PAGINATION_VALUE * (newPage - 1) : PAGINATION_VALUE)
      setCurrentPage(newPage)
      navigate(`/dashboard/groups/${newPage}`)
    }
  }

  React.useEffect(() => {
    document.title = "Dashboard"
  }, [])

  const noResults = formattedGroups.length < 1
  const noSearch = searchValue === undefined || searchValue === ""
  const noFilter = suborgFilter === undefined || suborgFilter.length < 1

  return (
    <Container>
      <HorizontalSpacer spacing={4} />
      <Row align="end">
        <Col lg={6} md={12} xs={12}>
          <Row justify="between" align="end">
            <Col lg={4}>
              <SearchField onChange={(e) => setSearchValue(e)} />
              <Visible xs md>
                <HorizontalSpacer spacing={4} />
              </Visible>
            </Col>
            <Col lg={4}>
              <Dropdown
                label="Sort by"
                options={DROPDOWN_OPTIONS}
                onSelectionChange={(id) => setSortingId(id)}
                selectedKey={sortingId}
              />
            </Col>
            <Col lg={4} />
          </Row>
        </Col>
        <Hidden xs md>
          <Col md={6}>
            <Row nogutter justify="end">
              <Link to="/dashboard">
                <PrimaryButton title="Data plans" />
              </Link>
              <Spacer spacing={1} />
              <SecondaryButton title="Groups" />
              <Spacer spacing={1} />
              <Link to="/dashboard/terminals">
                <PrimaryButton title="Terminals" />
              </Link>
            </Row>
          </Col>
        </Hidden>
      </Row>
      <Visible xs md>
        <div style={{ display: "flex", flexDirection: "column" }}>
          <HorizontalSpacer spacing={2} />
          <Link to="/dashboard">
            <PrimaryButton title="Data plans" fullWidth />
          </Link>
          <HorizontalSpacer spacing={2} />

          <SecondaryButton title="Groups" fullWidth />
          <HorizontalSpacer spacing={2} />

          <Link to="/dashboard/terminals">
            <PrimaryButton title="Terminals" fullWidth />
          </Link>
        </div>
      </Visible>
      <HorizontalSpacer spacing={3} />
      {isLoading ? (
        <LoadingSpinner />
      ) : noResults && noFilter && noSearch ? (
        <EmptyDashboardPage />
      ) : noResults ? (
        <NoSearchResultPage />
      ) : (
        <div>
          <div>
            {formattedGroups.map((group: GroupModel) => (
              <GroupOverviewCard group={group} key={group.id} />
            ))}
          </div>
          <HorizontalSpacer spacing={2} />
          <Row justify="center">
            <PaginationComponent
              itemsPerPageValues={PAGINATION_VALUE_PRESETS}
              pages={totalPages}
              currentPageIndex={currentPage}
              onClickPage={onClickPage}
            />
          </Row>
        </div>
      )}
      <HorizontalSpacer spacing={3} />
    </Container>
  )
}

export default GroupsPage
